import { DEFAULT_LOCALE, getPreferredLocale,SUPPORTED_LOCALES } from '@/services/locale.service';
import categoryTaxonomy from '@/shared/i18n/category-taxonomy.json';

function normalizeLocale(locale) {
  if (typeof locale !== 'string') {
    return DEFAULT_LOCALE;
  }

  const normalized = locale.trim().toLowerCase().split('-')[0];
  return SUPPORTED_LOCALES.includes(normalized) ? normalized : DEFAULT_LOCALE;
}

function normalizeValue(value) {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function normalizeLookupToken(value) {
  return normalizeValue(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .toLowerCase();
}

function getTypeConfig(type = '') {
  return categoryTaxonomy?.types?.[type] || null;
}

function listEntries(type = '') {
  const config = getTypeConfig(type);
  return Array.isArray(config?.entries) ? config.entries : [];
}

function findEntry(type = '', candidate = '') {
  const normalizedCandidate = normalizeLookupToken(candidate);
  if (!normalizedCandidate) {
    return null;
  }

  return listEntries(type).find((entry) => {
    const labels = entry?.labels && typeof entry.labels === 'object' ? Object.values(entry.labels) : [];
    const candidates = [entry?.slug, entry?.value, entry?.routeValue, ...labels];
    return candidates.some((current) => normalizeLookupToken(current) === normalizedCandidate);
  }) || null;
}

function getEntryLabel(entry, locale = DEFAULT_LOCALE) {
  if (!entry || typeof entry !== 'object') {
    return '';
  }

  const normalizedLocale = normalizeLocale(locale);
  return normalizeValue(entry.labels?.[normalizedLocale] || entry.labels?.[DEFAULT_LOCALE] || entry.value);
}

export function getCategoryDisplayName(type = '', locale = getPreferredLocale()) {
  const config = getTypeConfig(type);
  if (!config) {
    return '';
  }

  const normalizedLocale = normalizeLocale(locale);
  return normalizeValue(config.displayNames?.[normalizedLocale] || config.displayNames?.[DEFAULT_LOCALE] || type);
}

export function getCategoryDisplayEntries(type = '', locale = getPreferredLocale()) {
  return listEntries(type).map((entry) => ({
    slug: normalizeValue(entry.slug),
    value: normalizeValue(entry.value),
    routeValue: normalizeValue(entry.routeValue || entry.value),
    label: getEntryLabel(entry, locale),
  }));
}

export function getCategoryDisplayLabel(type = '', candidate = '', locale = getPreferredLocale()) {
  const entry = findEntry(type, candidate);
  if (!entry) {
    return normalizeValue(candidate);
  }

  return getEntryLabel(entry, locale);
}

export function getCategoryCanonicalValue(type = '', candidate = '') {
  const entry = findEntry(type, candidate);
  return normalizeValue(entry?.value || candidate);
}

export function getCategoryRouteValue(type = '', candidate = '') {
  const entry = findEntry(type, candidate);
  return normalizeValue(entry?.routeValue || entry?.value || candidate);
}
